import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Cell,
} from 'recharts';

const CATEGORIES = ['topic', 'methodology', 'findings', 'materials'];

function scoreToColor(score) {
  // Low score = light gray, high score = near black
  const lightness = Math.round(95 - score * 70);
  return `hsl(0,0%,${lightness}%)`;
}

function truncTitle(title, len = 20) {
  if (!title) return '?';
  return title.length > len ? title.slice(0, len) + '…' : title;
}

function buildScatterData(papers, scores) {
  const data = [];
  for (const score of scores) {
    const p1Idx = papers.findIndex((p) => p.id === score.paper1_id);
    const p2Idx = papers.findIndex((p) => p.id === score.paper2_id);
    if (p1Idx >= 0 && p2Idx >= 0) {
      data.push({
        x: p1Idx,
        y: p2Idx,
        z: Math.round(score.score * 100),
        score: score.score,
        categories: score.categories,
        p1: truncTitle(papers[p1Idx].title),
        p2: truncTitle(papers[p2Idx].title),
      });
      data.push({
        x: p2Idx,
        y: p1Idx,
        z: Math.round(score.score * 100),
        score: score.score,
        categories: score.categories,
        p1: truncTitle(papers[p2Idx].title),
        p2: truncTitle(papers[p1Idx].title),
      });
    }
  }
  return data;
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div
      className="bg-white rounded-lg p-3 text-xs"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
    >
      <p className="font-semibold text-[#171717] mb-1">
        {d.p1} / {d.p2}
      </p>
      <p className="text-[#4d4d4d]">Overall: {Math.round(d.score * 100)}%</p>
      {d.categories && (
        <div className="mt-1 space-y-0.5">
          {Object.entries(d.categories).map(([k, v]) => (
            <p key={k} className="text-[#808080]">
              {k}: {Math.round(v * 100)}%
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default function SimilarityMatrix({ result }) {
  if (!result) return null;

  const { papers, scores, mock_mode } = result;

  const scatterData = buildScatterData(papers, scores);
  const tickFormatter = (idx) => truncTitle(papers[idx]?.title || '', 15);

  return (
    <div
      className="bg-white rounded-lg p-4"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
    >
      <h2 className="text-lg font-semibold text-[#171717] mb-2" style={{ letterSpacing: '-0.32px' }}>Similarity Analysis</h2>

      {mock_mode && (
        <div className="mb-3 px-3 py-2 rounded text-xs font-medium bg-amber-50 text-amber-700" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
          MOCK MODE — AI analysis unavailable. Set ANTHROPIC_API_KEY in backend/.env for real analysis.
        </div>
      )}

      <div className="mb-4">
        <p className="text-xs text-[#808080] mb-2">
          Paper-pair similarity (bubble size = overall score)
        </p>
        <ResponsiveContainer width="100%" height={250}>
          <ScatterChart margin={{ top: 10, right: 30, bottom: 30, left: 60 }}>
            <XAxis
              type="number"
              dataKey="x"
              domain={[-0.5, papers.length - 0.5]}
              tickCount={papers.length}
              tickFormatter={tickFormatter}
              tick={{ fontSize: 11 }}
              angle={-20}
              interval={0}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[-0.5, papers.length - 0.5]}
              tickCount={papers.length}
              tickFormatter={tickFormatter}
              tick={{ fontSize: 11 }}
              width={80}
            />
            <ZAxis type="number" dataKey="z" range={[40, 400]} />
            <Tooltip content={<CustomTooltip />} />
            <Scatter data={scatterData}>
              {scatterData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={scoreToColor(entry.score)}
                  stroke="#ebebeb"
                  strokeWidth={1}
                  fillOpacity={0.9}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div>
        <p className="text-xs font-medium text-[#4d4d4d] mb-2">Category breakdown per pair:</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th className="text-left px-2 py-1.5 text-[#808080]" style={{ borderBottom: '1px solid #ebebeb' }}>Pair</th>
                {CATEGORIES.map((cat) => (
                  <th
                    key={cat}
                    className="text-center px-2 py-1.5 text-[#808080] capitalize"
                    style={{ borderBottom: '1px solid #ebebeb' }}
                  >
                    {cat}
                  </th>
                ))}
                <th className="text-center px-2 py-1.5 text-[#808080]" style={{ borderBottom: '1px solid #ebebeb' }}>
                  Overall
                </th>
              </tr>
            </thead>
            <tbody>
              {scores.map((score, idx) => {
                const p1 = papers.find((p) => p.id === score.paper1_id);
                const p2 = papers.find((p) => p.id === score.paper2_id);
                return (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td className="px-2 py-1.5 text-[#4d4d4d] max-w-xs" style={{ borderBottom: '1px solid #ebebeb' }}>
                      <span title={p1?.title}>{truncTitle(p1?.title)}</span>
                      {' / '}
                      <span title={p2?.title}>{truncTitle(p2?.title)}</span>
                    </td>
                    {CATEGORIES.map((cat) => (
                      <td
                        key={cat}
                        className="text-center px-2 py-1.5"
                        style={{
                          borderBottom: '1px solid #ebebeb',
                          backgroundColor: scoreToColor(score.categories?.[cat] ?? score.score),
                        }}
                      >
                        {Math.round((score.categories?.[cat] ?? score.score) * 100)}%
                      </td>
                    ))}
                    <td
                      className="text-center px-2 py-1.5 font-semibold"
                      style={{
                        borderBottom: '1px solid #ebebeb',
                        backgroundColor: scoreToColor(score.score),
                      }}
                    >
                      {Math.round(score.score * 100)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
